import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, User, Clock, ChevronRight, Tag } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';

interface BlogPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  tags: string[];
  image_url: string;
  published_at: string;
  read_time_minutes: number;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const months = [
    'jaanuar', 'veebruar', 'märts', 'aprill', 'mai', 'juuni',
    'juuli', 'august', 'september', 'oktoober', 'november', 'detsember'
  ];
  return `${d.getDate()}. ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    supabase
      .from('blog_posts')
      .select('id, slug, title, excerpt, author, tags, image_url, published_at, read_time_minutes')
      .lte('published_at', new Date().toISOString())
      .order('published_at', { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) {
          setPosts(data as BlogPost[]);
        }
        setLoading(false);
      });
  }, []);

  const allTags = Array.from(new Set(posts.flatMap(p => p.tags)));

  const filteredPosts = posts.filter(post => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      post.title.toLowerCase().includes(q) ||
      post.excerpt.toLowerCase().includes(q);
    const matchesTags =
      selectedTags.length === 0 ||
      selectedTags.some(tag => post.tags.includes(tag));
    return matchesSearch && matchesTags;
  });

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  return (
    <>
      <Helmet>
        <title>Nipid, Trikid & Uudised | Tõuks.ee Blogi</title>
        <meta name="description" content="Avasta tõuksimisega seotud nipid, trikid ja uudised. Õpetus algajatele ja profidele, hooldusõpetused ning Eesti parimad sõidukohad." />
        <meta name="keywords" content="tõuks blogi, trikitõuks nipid, tõuksi trikid, tõuks algajatele, tõuks hooldus, trikitõuks õpetus" />
        <link rel="canonical" href="https://xn--tuks-0qa.ee/blogi" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Nipid, Trikid & Uudised | Tõuks.ee Blogi" />
        <meta property="og:description" content="Avasta tõuksimisega seotud nipid, trikid ja uudised. Õpetus algajatele ja profidele." />
        <meta property="og:url" content="https://xn--tuks-0qa.ee/blogi" />
        <meta property="og:image" content="https://xn--tuks-0qa.ee/Touks-icon4.webp" />

        {/* BreadcrumbList */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Kodu", "item": "https://xn--tuks-0qa.ee/" },
            { "@type": "ListItem", "position": 2, "name": "Blogi", "item": "https://xn--tuks-0qa.ee/blogi" }
          ]
        })}</script>

        {/* Blog schema */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "Tõuks.ee Blogi",
          "description": "Nipid, trikid ja uudised trikitõuksisõitjatele",
          "url": "https://xn--tuks-0qa.ee/blogi",
          "inLanguage": "et",
          "publisher": {
            "@type": "Organization",
            "name": "Tõuks.ee",
            "url": "https://xn--tuks-0qa.ee",
            "logo": {
              "@type": "ImageObject",
              "url": "https://res.cloudinary.com/daka2lob2/image/upload/v1751272742/Touks_logo3_ed9w6g.png"
            }
          },
          "blogPost": posts.map(post => ({
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "author": { "@type": "Person", "name": post.author },
            "datePublished": post.published_at,
            "image": post.image_url || "https://xn--tuks-0qa.ee/Touks-icon4.webp",
            "url": `https://xn--tuks-0qa.ee/blogi/${post.slug}`,
            "keywords": post.tags.join(', '),
            "timeRequired": `PT${post.read_time_minutes}M`
          }))
        })}</script>

        {/* ItemList */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Tõuks.ee blogi postitused",
          "itemListElement": posts.map((post, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": post.title,
            "url": `https://xn--tuks-0qa.ee/blogi/${post.slug}`
          }))
        })}</script>
      </Helmet>

      <div className="container mx-auto py-12 px-4 md:px-6">
        {/* Hero */}
        <div className="relative mb-12">
          <div className="absolute inset-0 bg-gradient-to-r from-[#4FC1E9] to-blue-600 rounded-lg opacity-10"></div>
          <div className="relative bg-white/50 backdrop-blur-sm rounded-lg p-8 md:p-12">
            <div className="flex items-center gap-2 text-[#4FC1E9] mb-4">
              <BookOpen className="w-5 h-5" />
              <span className="font-medium">Tõuksi Maailm</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Nipid, Trikid & Uudised</h1>
            <p className="text-gray-600 text-lg max-w-2xl">
              Avasta tõuksi maailma saladusi, õpi uusi trikke ja püsi kursis värskete uudistega.
              Siin jagavad oma teadmisi nii algajad kui ka proffid!
            </p>
          </div>
        </div>

        {/* Filters */}
        {!loading && posts.length > 0 && (
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`px-4 py-2 rounded-full text-sm transition-colors duration-300 ${
                      selectedTags.includes(tag)
                        ? 'bg-[#4FC1E9] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Otsi postitusi..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full md:w-64 px-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#4FC1E9] focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-200"></div>
                <div className="p-6 space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && filteredPosts.length === 0 && (
          <div className="text-center py-24 text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">
              {searchQuery || selectedTags.length > 0
                ? 'Ühtegi postitust ei leitud.'
                : 'Artiklid on tulemas peagi!'}
            </p>
          </div>
        )}

        {/* Post grid */}
        {!loading && filteredPosts.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map(post => (
              <article
                key={post.id}
                className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <Link to={`/blogi/${post.slug}`} className="block">
                  <div className="relative h-48 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50"></div>
                    {post.image_url ? (
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#4FC1E9]/30 to-blue-200 flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-[#4FC1E9]/50" />
                      </div>
                    )}
                    {post.tags.length > 0 && (
                      <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
                        {post.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="bg-[#4FC1E9] text-white px-3 py-1 rounded-full text-sm">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <h2 className="text-xl font-bold mb-3 group-hover:text-[#4FC1E9] transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>

                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {post.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.read_time_minutes} min
                        </span>
                      </div>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(post.published_at)}
                      </span>
                    </div>

                    <div className="mt-4 flex items-center text-[#4FC1E9] font-medium group-hover:translate-x-2 transition-transform">
                      Loe edasi
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
